import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Grid, Typography, LinearProgress } from "@mui/material";
import Input from "../../common/Input";
import { resetPassword } from "../../../store/api/users";
import Button from "../../common/Button";
import { useNavigate } from "react-router-dom";
import Toast from "../../common/Toast";
import { Link } from "react-router-dom";

const Reset = () => {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    password: false,
    confirmPassword: false,
    samePassword: false,
    passwordStrength: false,
  });
  const [toast, setToast] = useState({ message: null, status: null });
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
    meetsRequirements: false,
  });

  useEffect(() => {
    const email = JSON.parse(localStorage.getItem("email"));
    const resetToken = JSON.parse(localStorage.getItem("resetToken"));
    setEmail(email);
    setResetToken(resetToken);
  }, []);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    if (!password) {
      return { score: 0, label: "", color: "", meetsRequirements: false };
    }

    let score = 0;
    const checks = {
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    // Calculate score
    if (checks.length) score += 25;
    if (checks.uppercase) score += 25;
    if (checks.lowercase) score += 25;
    if (checks.special) score += 25;

    // Determine label, color, and if requirements are met
    let label, color, meetsRequirements;

    if (score < 50) {
      label = "Weak";
      color = "#f44336"; // Red
      meetsRequirements = false;
    } else if (score < 75) {
      label = "Fair";
      color = "#ff9800"; // Orange
      meetsRequirements = false;
    } else if (score < 100) {
      label = "Good";
      color = "#2196f3"; // Blue
      meetsRequirements = false;
    } else {
      label = "Strong";
      color = "#4caf50"; // Green
      meetsRequirements = true;
    }

    // All requirements must be met for it to be considered valid
    meetsRequirements =
      checks.length && checks.uppercase && checks.lowercase && checks.special;

    return { score, label, color, meetsRequirements };
  };

  // Update password strength when password changes
  useEffect(() => {
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  }, [password]);

  const validateForm = () => {
    let hasError = false;
    const newErrors = {
      password: false,
      confirmPassword: false,
      samePassword: false,
      passwordStrength: false,
    };

    if (password === "") {
      newErrors.password = true;
      hasError = true;
    }

    if (!passwordStrength.meetsRequirements && password !== "") {
      newErrors.passwordStrength = true;
      hasError = true;
    }

    if (confirmPassword === "") {
      newErrors.confirmPassword = true;
      hasError = true;
    }

    if (confirmPassword !== password && confirmPassword !== "") {
      newErrors.samePassword = true;
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const SubmitForm = () => {
    if (validateForm()) {
      setToast({ message: null, status: null });

      ForgetPasswordMutate.mutate({
        newPassword: password,
        email,
        token: resetToken,
      });
    }
  };

  const ForgetPasswordMutate = useMutation({
    mutationFn: resetPassword,
    onSuccess: (data) => {
      setToast({ message: "Password updated successfully!", status: 200 });
      navigate("/");
    },
    onError: (error) => {
      let errorMessage = error.response?.data?.title || "Something went wrong.";
      let status = error.response?.status || 400;

      if (
        status === 401 ||
        errorMessage.toLowerCase().includes("invalid") ||
        errorMessage.toLowerCase().includes("expire")
      ) {
        setLinkExpired(true);
        errorMessage = "The link has expired.";
      }

      setToast({ message: errorMessage, status });
    },
  });

  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => {
        setToast({ message: null, status: null });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [toast.message]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        bgcolor: "white",
        padding: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: 400,
          width: "100%",
          textAlign: "center",
          border: "1px solid #ccc",
          borderRadius: 2,
          padding: 4,
          bgcolor: "background.paper",
          boxShadow: 3,
        }}
      >
        <Typography variant="h4" sx={{ mb: 3 }}>
          Reset Password
        </Typography>

        <Box sx={{ width: "100%" }}>
          <Grid
            container
            rowSpacing={2}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            {/* Password Requirements Note */}
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "left",
                  color: errors.passwordStrength ? "#f44336" : "#666",
                  fontSize: "0.875rem",
                  mb: 1,
                }}
              >
                Passwords must be more than 6 characters in length, and contain
                a mix of uppercase, lowercase, and special characters.
              </Typography>
            </Grid>

            {/* Error Messages */}
            {errors.samePassword && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ color: "red", textAlign: "left" }}
                >
                  Passwords do not match
                </Typography>
              </Grid>
            )}

            {linkExpired && (
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ color: "red" }}>
                  The link has expired. Click{" "}
                  <Link to="/forgetPassword" style={{ color: "blue" }}>
                    here
                  </Link>{" "}
                  to resend.
                </Typography>
              </Grid>
            )}

            {/* Password Input */}
            <Grid item xs={12}>
              <Input
                required={true}
                onChange={setPassword}
                value={password}
                label="Password"
                type="password"
                error={errors.password || errors.passwordStrength}
                helperText={
                  errors.password
                    ? "Password is required"
                    : errors.passwordStrength
                    ? "Password does not meet requirements"
                    : ""
                }
              />
            </Grid>

            {/* Password Strength Indicator */}
            {password && (
              <Grid item xs={12}>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.75rem", color: "#666" }}
                    >
                      Password Strength
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: passwordStrength.color,
                        fontWeight: "bold",
                      }}
                    >
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.score}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: passwordStrength.color,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* Confirm Password Input */}
            <Grid item xs={12}>
              <Input
                required={true}
                onChange={setConfirmPassword}
                value={confirmPassword}
                label="Confirm Password"
                type="password"
                error={errors.confirmPassword || errors.samePassword}
                helperText={
                  errors.confirmPassword
                    ? "Please confirm your password"
                    : errors.samePassword
                    ? "Passwords do not match"
                    : ""
                }
              />
            </Grid>

            {/* Submit Button */}
            <Grid
              display="flex"
              flexDirection="column"
              alignItems="center"
              item
              xs={12}
              sx={{ mt: 2 }}
            >
              <Button
                onClick={SubmitForm}
                text="Continue"
                disabled={
                  !passwordStrength.meetsRequirements ||
                  password !== confirmPassword
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      {toast.message && <Toast message={toast.message} status={toast.status} />}
    </Box>
  );
};

export default Reset;
